// CONTRACT:C2-RENDER-ENGINE.1.0
/** LOD + GPU-aggregation hooks (## Behavioral Contracts: "LOD + GPU aggregation
 *  by zoom" and "Allocation chunking").
 *
 *  Pure decision logic, no GPU: from the active zoom and entity count it decides
 *  whether to aggregate (H3) and at what resolution, and how many layer chunks
 *  the set must be split into to stay under the GPU alloc cap. The engine feeds
 *  these decisions to the backend; the actual H3 binning runs GPU-side.
 */

import type { LayerSpec } from "../../contracts-ts/src/index.ts";

/** ~1GB Chrome WebGL alloc cap (## Behavioral Contracts: allocation chunking). */
export const DEFAULT_GPU_ALLOC_CAP_BYTES = 1_000_000_000;

/** Conservative per-entity GPU footprint (position + color + size attrs). */
export const APPROX_BYTES_PER_ENTITY = 64;

/** A render plan for one layer at the current view. */
export interface LodPlan {
  /** Whether to GPU-aggregate to H3 hexes rather than draw discrete instances. */
  aggregate: boolean;
  /** H3 resolution (0..15) when aggregating; finer as zoom increases. */
  h3Resolution: number;
  /** Number of layer chunks to split the set across (alloc-cap chunking). */
  chunks: number;
}

/** Map a MapLibre zoom (0..22) to an H3 resolution (0..15), coarser when zoomed out. */
export function h3ResolutionForZoom(zoom: number): number {
  const z = Math.max(0, Math.min(22, zoom));
  // Roughly: zoom 0 -> res 0, zoom 22 -> res 15. Monotonic, clamped.
  return Math.max(0, Math.min(15, Math.round((z / 22) * 15)));
}

/** Decide the LOD plan for a layer given its spec, the active zoom, and count.
 *  - Aggregates when the spec opts into H3 AND density justifies it.
 *  - Chunks when the discrete buffer would approach the GPU alloc cap. */
export function planLod(
  spec: LayerSpec,
  zoom: number,
  entityCount: number,
  capBytes: number = DEFAULT_GPU_ALLOC_CAP_BYTES,
): LodPlan {
  const wantsAggregate = spec.lod?.aggregate === "h3";
  const byZoom = spec.lod?.byZoom !== false; // default on when lod present
  // Aggregate dense sets; below ~50k discrete instances are cheap enough.
  const aggregate = wantsAggregate && (!byZoom || entityCount > 50_000);
  const bytes = entityCount * APPROX_BYTES_PER_ENTITY;
  const chunks = Math.max(1, Math.ceil(bytes / capBytes));
  return {
    aggregate,
    h3Resolution: h3ResolutionForZoom(zoom),
    chunks,
  };
}
